import { MicrophoneIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { CrownIcon } from "@/components/common/icons/Solid";
import { orderBy } from "lodash";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Actions as ACTIONS } from "../../store/actions/permissionActions";
import InvalidPermissions from "../company-pages/InvalidPermissions";
import UpgradeRequired from "../company-pages/UpgradeRequired";
import PermissionsTable from "./PermissionsTable";
import { useMemo } from "react";
import InviteManagerModal from "./InviteManagerModal";

const Permissions = ({
	permissions,
	getPermissions,
	accountOwner,
	authorisedUsers,
	isAdmin,
	advisor,
	pointOfContact,
	getMyPermissions,
	userPermissions,
	includedInPlan,
	actionOnPerm,
	openActionOnPermModal,
	updatePOC,
	updateAccountOwner,
	revokeAccess,
	isOwner,
	isDemo,
	profile,
}) => {
	useEffect(() => {
		if (!permissions) {
			getPermissions();
			getMyPermissions();
		}
	}, []);

	if (!includedInPlan) return <UpgradeRequired advisor={true} />;
	if (userPermissions == "None") return <InvalidPermissions />;

	return (
		<>
			{actionOnPerm ? (
				<ActionOnPermissionModal
					{...{
						closeModal: () => openActionOnPermModal(false),
						openModal: actionOnPerm,
						authorisedUsers,
						accountOwner,
						pointOfContact,
						updatePOC,
						revokeAccess,
						updateAccountOwner,
						profile,
					}}
				/>
			) : null}
			{isDemo ? null : <InviteManagerModal />}

			<div className="content">
				<div className="col-xs-6 pl-0">
					<h5>Account Owner: {accountOwner?.name || "None Set"}</h5>
					<p className="text-muted">
						This is the owner of the account, to transfer account ownership contact support.
					</p>
				</div>
				<div className="col-xs-6 pl-0">
					{isAdmin ? (
						<button
							className="btn btn-default"
							style={{ marginBottom: "1rem" }}
							type="button"
							onClick={() => {
								openActionOnPermModal("Change Account Owner");
							}}
						>
							<CrownIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							<div style={{ float: "left" }}>Transfer Ownership</div>
						</button>
					) : null}
				</div>
				<div className="clearfix" />
				<div className="col-xs-6 pl-0">
					<h5>Point of Contact: {pointOfContact?.name}</h5>
					<p className="text-muted">
						This is the contact who will be receiving emails and communications from support.
					</p>
				</div>
				<div className="col-xs-6 pl-0">
					{userPermissions != "Read" && (
						<button
							className="btn btn-default"
							style={{ marginBottom: "1rem" }}
							onClick={() => {
								openActionOnPermModal("Change Point of Contact");
							}}
						>
							<MicrophoneIcon style={{ height: "1.5em", float: "left", marginRight: 5 }} />
							<div style={{ float: "left" }}>Change Point of Contact</div>
						</button>
					)}
				</div>

				<div className="clearfix" />
				{/* <h5>Advisor: {advisor ? advisor.name : 'No Advisor Set'}</h5>
        <p>To Change advisor (accountant) taking care of this account please contact support.</p> */}

				<h4>Authorised Manager Permissions</h4>
				{authorisedUsers ? <PermissionsTable /> : <p>No manager permissions set</p>}
			</div>
			<div className="clearfix" />
		</>
	);
};

const ActionOnPermissionModal = ({
	openModal,
	closeModal,
	authorisedUsers,
	accountOwner,
	pointOfContact,
	updatePOC,
	revokeAccess,
	updateAccountOwner,
	profile,
}) => {
	const [selectedUser, setSelectedUser] = useState(null);
	const [user, setUser, listFilter, color] = useMemo(() => {
		switch (openModal) {
			case "Change Point of Contact":
				return [pointOfContact, updatePOC, (user) => !user?.pointOfContact];
			case "Change Account Owner":
				return [accountOwner, updateAccountOwner, (user) => user?.status != "Owner"];
			case "Revoke Access":
				return [null, revokeAccess, (user) => user?.status != "Owner", "danger"];
			default:
				return [];
		}
	}, [openModal]);

	if (!openModal) return null;
	return (
		<div
			className="modal"
			tabIndex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)", overflow: "auto" }}
		>
			<div
				className="modal-dialog"
				style={{ width: "50%" }}
			>
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div
							className={`block-header bg-${color ?? "success"}`}
							style={{ padding: 20, position: "sticky", top: 0, zIndex: 50 }}
						>
							<ul className="block-options">
								<li>
									<button
										type="button"
										onClick={closeModal}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3 className="block-title">{openModal}</h3>
						</div>
					</div>
					<div
						className="block-content"
						style={{ padding: 20 }}
					>
						{user && (
							<h5>
								{openModal}: {user?.name || "None Set"}
							</h5>
						)}
						<table
							className="table table-hover no-footer"
							role="grid"
						>
							<thead>
								<tr>
									<th width="5%"></th>
									<th>User</th>
								</tr>
							</thead>
							<tbody>
								{authorisedUsers?.filter(listFilter)?.map((user) => {
									return (
										<UserRow
											user={user}
											selected={selectedUser}
											updateSelected={setSelectedUser}
											color={color}
											profileId={profile?.id}
										/>
									);
								})}
							</tbody>
						</table>
						<div className="col-xs-12">
							<button
								className={`btn btn-${color ?? "success"} pull-right`}
								disabled={!selectedUser}
								onClick={() => {
									setUser(selectedUser);
								}}
							>
								{openModal}
							</button>
						</div>
						<div className="clearfix" />
					</div>
				</div>
			</div>
		</div>
	);
};

const UserRow = ({ user, selected, updateSelected, profileId, color }) => {
	return (
		<tr>
			<td>
				<label className={`css-input css-checkbox css-checkbox-${color ?? "success"}`}>
					<input
						type="checkbox"
						id={`checkbox-${user.id}`}
						checked={selected === user.id}
						onChange={(e) => {
							if (selected === user.id) updateSelected(null);
							else updateSelected(user.id);
						}}
					/>
					<span />
				</label>
			</td>
			<td>
				{user.name}
				{user?.id === profileId ? " (You)" : ""}
			</td>
		</tr>
	);
};

const mapStateToProps = (state) => {
	const userPermissions = state?.permissions?.my?.permissions?.permissions;
	const { id: advisorId, plan } = { ...state.advisors?.settings };
	const authorisedUsers = orderBy(state.permissions?.authorisedUsers || [], ["status", "pointOfContact", "name"], ["desc", "asc", "asc"]);
	const accountOwner = authorisedUsers?.find((user) => user?.status === "Owner");
	const pointOfContact = authorisedUsers?.find((user) => user?.pointOfContact) || accountOwner;
	const profile = state.my?.profile;

	return {
		profile,
		isAdmin: state.my.profile.isAdmin,
		accountOwner,
		userPermissions,
		authorisedUsers,
		pointOfContact,
		isOwner: accountOwner?.userId === profile?.id,
		isDemo: state.company?.settings?.isDemo,
		actionOnPerm: state.permissions?.actionOnPermModal,
		includedInPlan: state.permissions?.plan?.permissions,
		// advisorHasPlan: state?.my?.profile?.isAdvisor
		// 	? ["89db58e5-c640-4dce-a329-097b61029194"].includes(String(plan)) &&
		// 	  state.company?.settings?.advisor == advisorId
		// 	: false,
	};
};

const mapDispatchToProps = (dispatch) => ({
	inviteUserModal: () => dispatch(ACTIONS.inviteUserModal()),
	inviteUser: () => dispatch(ACTIONS.inviteUser()),
	savePermissions: () => dispatch(ACTIONS.savePermissions()),
	getPermissions: () => dispatch(ACTIONS.getPermissions()),
	getMyPermissions: () => dispatch(ACTIONS.getMyPermissions()),
	updatePOC: (user) => dispatch(ACTIONS.updatePOC(user)),
	updateAccountOwner: (user) => dispatch(ACTIONS.updateAccountOwner(user)),
	revokeAccess: (user) => dispatch(ACTIONS.revokeAccess(user)),
	openActionOnPermModal: (status) => dispatch(ACTIONS.openActionOnPermModal(status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Permissions);
